import React, { useState, useEffect, FormEvent, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { PageProps } from "../types/auth";
import { trackPageVisit } from "../utils/pageTracker";

import styles from './styles/AccomplishmentDetailsPage.module.css';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

interface Accomplishment {
  accomplishment_id: number;
  name: string;
}

interface RouteParams {
  [key: string]: string | undefined;
  accomplishment_id?: string;
}

const AccomplishmentDetailsPage: React.FC<PageProps> = ({ token, selectedOrgID }) => {
  const { accomplishment_id } = useParams<RouteParams>();
  const [name, setName] = useState<string>("");
  const [error, setError] = useState<string>("");
  const navigate = useNavigate();
  const hasTrackedRef = useRef(false);

  // Track page visit once we have the accomplishment name
  useEffect(() => {
    if (name && !hasTrackedRef.current) {
      trackPageVisit(
        accomplishment_id ? `Accomplishment: ${name}` : "New Accomplishment",
        accomplishment_id ? `/accomplishments/${accomplishment_id}` : "/accomplishments/create"
      );
      hasTrackedRef.current = true;
    }
  }, [name, accomplishment_id]);

  useEffect(() => {
    if (accomplishment_id) {
      const fetchAccomplishment = async () => {
        try {
          const response = await fetch(`${API_BASE_URL}/api/accomplishments/${accomplishment_id}`, {
            method: "GET",
            headers: {
              Authorization: `Bearer ${token}`,
              "X-Org-ID": selectedOrgID?.toString() || "",
            },
          });

          if (!response.ok) {
            const errorData = await response.json();
            setError(errorData.error || "Failed to fetch accomplishment.");
            return;
          }

          const data: Accomplishment = await response.json();
          if (data) {
            setName(data.name || "");
          } else {
            setError("No data received for the accomplishment.");
          }
        } catch (err) {
          setError("An unexpected error occurred.");
        }
      };

      fetchAccomplishment();
    }
  }, [accomplishment_id, token, selectedOrgID]);

  const handleSaveOrUpdate = async (e: FormEvent) => {
    e.preventDefault();
    if (!name.trim()) {
      setError("Accomplishment name cannot be empty.");
      return;
    }

    try {
      const response = await fetch(
        `${API_BASE_URL}/api/accomplishments${accomplishment_id ? `/${accomplishment_id}` : ""}`,
        {
          method: accomplishment_id ? "PUT" : "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
            "X-Org-ID": selectedOrgID?.toString() || "",
          },
          body: JSON.stringify({
            name,
            org_id: selectedOrgID ? parseInt(selectedOrgID.toString(), 10) : 0,
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        setError(errorData.error || "Failed to save accomplishment.");
        return;
      }

      navigate("/accomplishments");
    } catch (err) {
      setError("An unexpected error occurred.");
    }
  };

  return (
    <div className={styles.container}>
      <h1 className={styles.title}>{accomplishment_id ? "Edit Accomplishment" : "New Accomplishment"}</h1>
      {error && <p className={styles.errorMessage}>{error}</p>}
      <form onSubmit={handleSaveOrUpdate} className={styles.form}>
        <div className={styles.formGroup}>
          <label className={styles.label}>Accomplishment Name:</label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            className={styles.input}
          />
        </div>
        <button type="submit" className={styles.submitButton}>
          {accomplishment_id ? "Update" : "Save"}
        </button>
      </form>
    </div>
  );
};

export default AccomplishmentDetailsPage;
